/**
 * Data layer for `advertiser-widgets`. Thin typed fetch wrappers over
 * /api/v1/advertiser-widgets. Raw `fetch()` is confined to this file per
 * the dash contract.
 *
 * BE surface (backed by apps/api/src/routes/advertiser-widgets.ts):
 *   GET    /layout/:advertiser_id  → { advertiser_id, count, widgets[] }
 *   POST   /                        → WidgetView
 *   PUT    /:id                     → WidgetView
 *   DELETE /:id                     → { id, deleted }
 *   POST   /reorder                 → { advertiser_id, count, widgets[] }
 */

export type WidgetKind = "kpi" | "chart" | "table";
export type WidgetWidth = 1 | 2 | 3 | 4;

export interface WidgetView {
	id: string;
	advertiser_id: string;
	kind: WidgetKind;
	title: string;
	config: Record<string, unknown>;
	position: number;
	width: WidgetWidth;
	enabled: boolean;
	created_at: number;
}

export interface LayoutResponse {
	advertiser_id: string;
	count: number;
	widgets: WidgetView[];
}

export interface CreateWidgetBody {
	advertiser_id: string;
	kind: WidgetKind;
	title: string;
	config?: Record<string, unknown>;
	width?: WidgetWidth;
	enabled?: boolean;
}

export interface UpdateWidgetBody {
	title?: string;
	kind?: WidgetKind;
	config?: Record<string, unknown>;
	width?: WidgetWidth;
	enabled?: boolean;
}

export interface ReorderBody {
	advertiser_id: string;
	ids: string[];
}

const BASE = "/api/v1/advertiser-widgets";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function getLayout(advertiserId: string): Promise<LayoutResponse> {
	const res = await fetch(`${BASE}/layout/${encodeURIComponent(advertiserId)}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<LayoutResponse>(res);
}

export async function createWidget(body: CreateWidgetBody): Promise<WidgetView> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<WidgetView>(res);
}

export async function updateWidget(
	id: string,
	body: UpdateWidgetBody,
): Promise<WidgetView> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "PUT",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<WidgetView>(res);
}

export async function deleteWidget(id: string): Promise<{ id: string; deleted: true }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ id: string; deleted: true }>(res);
}

export async function reorderWidgets(body: ReorderBody): Promise<LayoutResponse> {
	const res = await fetch(`${BASE}/reorder`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<LayoutResponse>(res);
}

export const advertiserWidgetsQueryKeys = {
	all: ["advertiser-widgets"] as const,
	layout: (advertiserId: string) =>
		[...advertiserWidgetsQueryKeys.all, "layout", advertiserId] as const,
	detail: (id: string) => [...advertiserWidgetsQueryKeys.all, id] as const,
};
